import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useUser } from '@clerk/clerk-react';
import { isAdminUser } from '../utils/adminAccess.js';

const STORAGE_KEY = 'upgraded.adminViewAs';
const ImpersonationContext = createContext(null);

function readStoredUser() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.email || !String(parsed.email).includes('@')) return null;
    return {
      id: parsed.id || parsed.email,
      email: parsed.email,
      fullName: parsed.fullName || '',
      label: parsed.label || parsed.email,
    };
  } catch {
    return null;
  }
}

function writeStoredUser(user) {
  if (typeof window === 'undefined') return;
  if (!user) {
    window.sessionStorage.removeItem(STORAGE_KEY);
    return;
  }
  window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(user));
}

export function ImpersonationProvider({ children }) {
  const { user, isLoaded } = useUser();
  const isAdmin = Boolean(isLoaded && isAdminUser(user));
  const [impersonatedUser, setImpersonatedUserState] = useState(readStoredUser);

  useEffect(() => {
    if (!isLoaded || isAdmin) return;
    setImpersonatedUserState(null);
    writeStoredUser(null);
  }, [isAdmin, isLoaded]);

  const setImpersonatedUser = useCallback((next) => {
    if (!isAdminUser(user)) return;
    if (!next?.email) {
      writeStoredUser(null);
      setImpersonatedUserState(null);
      return;
    }
    const value = {
      id: next.id || next.value || next.email,
      email: next.email,
      fullName: next.fullName || '',
      label: next.label || next.email,
    };
    writeStoredUser(value);
    setImpersonatedUserState(value);
  }, [user]);

  const value = useMemo(() => {
    const active = isAdmin ? impersonatedUser : null;
    return {
      isAdmin,
      impersonatedUser: active,
      isImpersonating: Boolean(active?.email),
      setImpersonatedUser,
    };
  }, [isAdmin, impersonatedUser, setImpersonatedUser]);

  return (
    <ImpersonationContext.Provider value={value}>
      {children}
    </ImpersonationContext.Provider>
  );
}

export function useImpersonation() {
  const context = useContext(ImpersonationContext);
  if (!context) {
    return {
      isAdmin: false,
      impersonatedUser: null,
      isImpersonating: false,
      setImpersonatedUser: () => {},
    };
  }
  return context;
}
